import { SvgIcon } from "@mui/material";
import React, { useContext } from 'react';
import { ReactComponent as LogoSvg } from "../logo.svg";
import { ThemeContext } from "./ThemeContext";

export type LogoLayout = 'full' | 'icon';

interface LogoProps {
  height?: number;
  useCustom?: boolean;
  /**
   * Which logo treatment to render.
   * - 'full' (default): the standard wordmark / full logo.
   * - 'icon': the compact icon mark (falls back to full logo when icon assets are missing).
   */
  layout?: LogoLayout;
  /**
   * Which logo treatment to render.
   * - 'auto' (default): follow the active theme (dark theme => dark-background logo).
   * - 'dark': force the logo intended for dark backgrounds (e.g. on the header app bar).
   * - 'light': force the logo intended for light backgrounds.
   */
  variant?: 'auto' | 'light' | 'dark';
}

const Logo: React.FC<LogoProps> = (props) => {
  const { darkMode, brandingSettings } = useContext(ThemeContext);
  const { useCustom = true, variant = 'auto', layout = 'full' } = props;

  // Resolve whether we're rendering against a dark background. Explicit variant
  // overrides the theme so callers (like the header) can contrast their own surface.
  const onDarkBackground = variant === 'auto' ? darkMode === true : variant === 'dark';

  const logoUrl = brandingSettings?.logoUrl;
  const logoDarkUrl = brandingSettings?.logoDarkUrl;
  const logoIconUrl = brandingSettings?.logoIconUrl;
  const logoIconDarkUrl = brandingSettings?.logoIconDarkUrl;

  const fullLogo = onDarkBackground
    ? (logoDarkUrl || logoUrl)
    : (logoUrl || logoDarkUrl);

  const iconLogo = onDarkBackground
    ? (logoIconDarkUrl || logoIconUrl)
    : (logoIconUrl || logoIconDarkUrl);

  const customLogo = layout === 'icon'
    ? (iconLogo || fullLogo)
    : fullLogo;

  // Soft edge lift: deeper on dark surfaces (hero/header), tighter on light.
  const dropShadow = onDarkBackground
    ? 'drop-shadow(0 2px 6px rgba(0, 0, 0, 0.55))'
    : 'drop-shadow(0 1px 1px rgba(24, 17, 17, 0.6))';

  // If custom logo is requested and available, render image
  if (useCustom && customLogo) {
    return (
      <img
        src={customLogo}
        alt="Custom Logo"
        style={{
          height: props.height ? `${props.height}px` : '100%',
          maxWidth: '100%',
          objectFit: 'contain',
          filter: dropShadow,
        }}
      />
    );
  }

  // Otherwise render original SVG
  const toPass = {
    sx: {
      height: '100%',
      maxWidth: '100%',
      filter: dropShadow,
      fill: onDarkBackground ? '#877010' : '#2a74b7'
    }
  };
  if (props.height && props.height > 0) {
    // @ts-ignore
    toPass.sx.fontSize = props.height;
  }

  return <SvgIcon
    {...toPass}
    component={LogoSvg}
    viewBox="0 0 591.7 693.3"
    inheritViewBox
  />
};

export default Logo;
